import { nanoid } from 'nanoid';
import { Queries } from 'tinybase';
import { WsSynchronizer } from 'tinybase/synchronizers/synchronizer-ws-client';
import { CellListener, MergeableStore } from 'tinybase/with-schemas';

import { BaseUserStore } from '~/common/tinybase/BaseUserStore';
import {
  MessagePayloadType,
  UserMessage,
  UserMessageStatus,
  UserMessageType,
} from '~/common/types/messages';
import { PathPersister, UserStoreSchemas } from '~/common/types/tinybase';

import { clientUserMessageHandlers, ClientUserMessageHandlers } from './clientUserMessageHandlers';
import { deletePersistedData } from './persister/helpers';

type HandleAndDispatchMessageOpts = {
  newEntityTempId?: ScalarId;
  newEntityParentTempId?: ScalarId;
};

export class ClientUserStore extends BaseUserStore {
  private messageHandlers: ClientUserMessageHandlers = clientUserMessageHandlers(this);

  private isConnected: boolean = true; // WebSocket is already open when the instance is constructed

  private hasSynced = false; // Becomes true once the initial startSync handshake completes

  private isReconciling = false; // Guards against overlapping reconciles

  private reconcilePromise?: Promise<void>; // The in-flight reconcile, shared by concurrent callers

  public constructor(
    userId: string,
    store: MergeableStore<UserStoreSchemas>,
    persister: PathPersister | undefined,
    public readonly synchronizer: WsSynchronizer<WebSocket>,
    public readonly queries: Queries,
  ) {
    super(userId, store, persister, synchronizer);

    const webSocket = synchronizer.getWebSocket();

    webSocket.addEventListener('open', () => this.setIsConnected(true));
    webSocket.addEventListener('close', () => this.setIsConnected(false));

    this.store.addCellListener('messages', null, 'status', this.messageChangeHandler, true);
  }

  public async startSync() {
    await this.synchronizer?.startSync(this.store.getContent());
    this.hasSynced = true;
  }

  public handleAndDispatchMessage<TMessageType extends UserMessageType>(
    type: TMessageType,
    entityId: ScalarId,
    payload: MessagePayloadType<TMessageType>,
    { newEntityTempId = '', newEntityParentTempId = '' }: HandleAndDispatchMessageOpts = {},
  ) {
    const message: UserMessage = {
      type,
      entityId: String(entityId),
      newEntityTempId: String(newEntityTempId),
      dependencyId: '',
      messageId: nanoid(),
      payload,
      timestamp: new Date().toISOString(),
      status: newEntityParentTempId
        ? UserMessageStatus.AWAITING_DEPENDENCY
        : UserMessageStatus.PENDING,
      error: '',
      wasCreatedOffline: !this.isConnected,
    };

    this.store.transaction(() => {
      this.messageHandlers[type](payload);
      this.put('messages', message);

      if (newEntityTempId) {
        this.addTempIdMap(newEntityTempId, newEntityParentTempId);
      }
    });
  }

  public hasPendingMessages() {
    return (<UserMessage[]>Object.values(this.getTable('messages'))).some(({ status }) =>
      [UserMessageStatus.AWAITING_DEPENDENCY, UserMessageStatus.PENDING].includes(status),
    );
  }

  private messageChangeHandler: CellListener<UserStoreSchemas, 'messages', null, 'status'> = (
    _changedStore,
    tableId,
    rowId,
    cellId,
    newCell,
    oldCell,
    _getCellChange,
  ) => {
    console.log('Row changed', tableId, rowId, cellId, oldCell, '->', newCell);

    if (cellId === 'status' && newCell === UserMessageStatus.COMPLETED) {
      console.log('Server confirmed update for', rowId);
    } else if (cellId === 'status' && newCell === UserMessageStatus.SUPERSEDED) {
      console.log('Server skipped message superseded by a newer write', rowId);
    } else if (cellId === 'status' && newCell === UserMessageStatus.FAILED) {
      console.error('Server failed to process message', rowId);
    }
  };

  public setIsConnected = (isConnected: boolean) => {
    this.isConnected = isConnected;

    if (isConnected && this.hasSynced) {
      void this.reconcile();
    }
  };

  // The synchronizer only reconciles during the initial startSync handshake, so
  // pull server changes (and re-push pending local ones) manually. Concurrent
  // callers share the in-flight run
  private reconcile = (): Promise<void> => {
    if (this.isReconciling) {
      return this.reconcilePromise ?? Promise.resolve();
    }
    this.isReconciling = true;

    this.reconcilePromise = (async () => {
      try {
        await this.synchronizer?.load();

        if (this.hasPendingMessages()) {
          await this.synchronizer?.save();
        }
      } catch (err) {
        console.warn('Failed to reconcile user store', err);
      } finally {
        this.isReconciling = false;
        this.reconcilePromise = undefined;
      }
    })();

    return this.reconcilePromise;
  };

  /** Deletes all data persisted on the client */
  public async purgeData() {
    void this.persister?.stopAutoSave();
    await deletePersistedData('tinybase-user');
  }
}
